import { FE_ADD_ON_ID, payflowTrackActionsService } from '@ctct/domain-layer';
import { AccountModificationFlowId, envs } from '@ctct/infrastructure-layer';
import { ProgressBarStepId } from '@ctct/state-client-layer';

import type { Listeners } from '../helpers';
import { controllerHelpers } from '../helpers';
import type {
  AddOnsManager,
  ContactsTierSelectManager,
  EmailPlansManager,
  InboundPromoCodesManager,
  NonProfitManager,
  OrderSummaryManager,
  PayflowProgressBarManager,
  PayflowTopBarManager,
  PrepaySelectManager,
  PromoCodesManager,
  RouterNonUiManager,
} from '../managers';
import { payflowProgressBarEventBus } from '../managers';
import { myAccountAppPaths } from '../managers/RouterNonUiManager/constants';

export class PayflowProgressBarController {
  public listeners: Listeners;

  private isHandlingRouteChange = false;

  constructor(
    private routerManager: RouterNonUiManager,
    private payflowProgressBarManager: PayflowProgressBarManager,
    private emailPlansManager: EmailPlansManager,
    private prepayManager: PrepaySelectManager,
    private contactsTierManager: ContactsTierSelectManager,
    private promoCodesManager: PromoCodesManager,
    private orderSummaryManager: OrderSummaryManager,
    private inboundPromoCodesManager: InboundPromoCodesManager | undefined,
    private addOnsManager: AddOnsManager | undefined,
    private nonProfitManager: NonProfitManager,
    private payflowTopBarManager: PayflowTopBarManager
  ) {
    this.listeners = controllerHelpers.createListeners([payflowProgressBarEventBus.bindTo('goToStep', this.goToStep)]);
  }

  public async init() {
    if (this.isHandlingRouteChange) return;

    this.isHandlingRouteChange = true;

    try {
      const routerManagerApi = await this.routerManager.getApiWhenReady();

      const route = routerManagerApi.getRoute();

      const cleanRoute = this.validateAndCleanRoute(route);

      if (!cleanRoute) return;

      const progressBarApi = await this.payflowProgressBarManager.getApiWhenReady();
      const emailPlansManagerApi = await this.emailPlansManager.getApiWhenReady();
      const selectedEmailPlanId = emailPlansManagerApi.getSelectedPlanId();

      const urlParamValues = await this.getUrlParamValues();
      const currentStep = progressBarApi.getActiveStep();

      switch (cleanRoute) {
        case myAccountAppPaths.checkout:
          // handle checkout direct route
          if (selectedEmailPlanId) {
            const isSecureCheckout = this.payflowProgressBarManager.flags?.isSecureCheckout;

            // In non-secure my-account flows, redirect to the "secure" checkout (secure or billing app).
            // When we're already in a secure-checkout context (secure or billing checkout pages),
            // avoid redirecting again to prevent refresh loops.
            if (!envs.isSecure() && !isSecureCheckout) {
              this.isHandlingRouteChange = false;
              await this.goToStep(ProgressBarStepId.CHECKOUT, progressBarApi.getFlowId()); // go to the secure checkout

              return;
            }

            if (currentStep !== ProgressBarStepId.CHECKOUT) {
              progressBarApi.goToCheckout();
            }
          } else {
            progressBarApi.goToPlans();
            routerManagerApi.goToPayflowEntry({
              replace: true,
              urlParams: routerManagerApi.convertStoreValuesToUrlParams(urlParamValues),
            });
          }

          return;

        case myAccountAppPaths.paymentPlan:
          if (selectedEmailPlanId) {
            if (currentStep !== ProgressBarStepId.PAYMENT_PLAN) {
              progressBarApi.goToPaymentPlans();
            }
          } else {
            progressBarApi.goToPlans();
            routerManagerApi.goToPayflowEntry({ replace: true, keepParams: true });
          }

          break;

        case myAccountAppPaths.paymentPlanSelect:
          if (currentStep !== ProgressBarStepId.PAYMENT_PLAN_SELECT) {
            progressBarApi.goToPaymentPlanSelect();
          }

          break;

        case myAccountAppPaths.paymentPlanCheckout:
          if (currentStep !== ProgressBarStepId.CHECKOUT) {
            progressBarApi.goToCheckout();
          }

          break;

        case myAccountAppPaths.addOns:
          // handle addons direct route
          if (selectedEmailPlanId) {
            if (currentStep !== ProgressBarStepId.ADD_ONS) {
              progressBarApi.goToAddOns();
            }
          } else {
            progressBarApi.goToPlans();
            routerManagerApi.goToPayflowEntry({
              replace: true,
              keepParams: true,
              urlParams: routerManagerApi.convertStoreValuesToUrlParams({
                ...urlParamValues,
                skipParams: ['emailPlanId'],
              }),
            });
          }

          break;

        default:
          // do nothing
          break;
      }
    } finally {
      this.isHandlingRouteChange = false;
    }
  }

  private goToStep = async (stepId: ProgressBarStepId, flowId: AccountModificationFlowId) => {
    if (this.isHandlingRouteChange) return;

    const routerManagerApi = await this.routerManager.getApiWhenReady();
    const urlParamValues = await this.getUrlParamValues();

    // Step-to-method mapping
    const stepActions: Record<ProgressBarStepId, () => void> = {
      [ProgressBarStepId.ADD_ONS]: () =>
        routerManagerApi.goToAddOnsStep({
          keepParams: true,
          urlParams: routerManagerApi.convertStoreValuesToUrlParams(urlParamValues),
        }),
      [ProgressBarStepId.CHECKOUT]: async () => {
        const addOnsManagerApi = await this.addOnsManager?.getApiWhenReady();
        const selectedAddons = addOnsManagerApi?.getSelectedAddons() ?? [];

        const isPaymentPlanFlow = flowId === AccountModificationFlowId.ChangePaymentPlan;

        if (selectedAddons.some((addon) => addon.id === FE_ADD_ON_ID.XERO)) {
          await payflowTrackActionsService.goingToCheckoutWithXeroAddonSelected();

          // wait 100ms to make sure the event is tracked
          setTimeout(() => {
            if (isPaymentPlanFlow) {
              routerManagerApi.goToPaymentPlanCheckoutStep({
                urlParams: routerManagerApi.convertStoreValuesToUrlParams(urlParamValues),
              });
            } else {
              routerManagerApi.goToCheckoutStep({
                urlParams: routerManagerApi.convertStoreValuesToUrlParams(urlParamValues),
              });
            }
          }, 100);

          return;
        }

        if (isPaymentPlanFlow) {
          routerManagerApi.goToPaymentPlanCheckoutStep({
            urlParams: routerManagerApi.convertStoreValuesToUrlParams(urlParamValues),
          });
        } else {
          routerManagerApi.goToCheckoutStep({
            urlParams: routerManagerApi.convertStoreValuesToUrlParams(urlParamValues),
          });
        }
      },
      [ProgressBarStepId.PAYMENT_PLAN]: () =>
        routerManagerApi.goToPaymentPlanStep({
          keepParams: true,
          urlParams: routerManagerApi.convertStoreValuesToUrlParams(urlParamValues),
        }),
      [ProgressBarStepId.PAYMENT_PLAN_SELECT]: () => {
        if (flowId === AccountModificationFlowId.ChangePaymentPlan) {
          return routerManagerApi.goToChangePaymentPlanStep({
            keepParams: true,
            urlParams: routerManagerApi.convertStoreValuesToUrlParams(urlParamValues),
          });
        }

        return routerManagerApi.goToPayflowPaymentPlanSelectStep({
          keepParams: true,
          urlParams: routerManagerApi.convertStoreValuesToUrlParams(urlParamValues),
        });
      },
      [ProgressBarStepId.SELECT_PLAN]: async () =>
        routerManagerApi.goToPayflowEntry({
          keepParams: true,
          urlParams: routerManagerApi.convertStoreValuesToUrlParams({
            ...urlParamValues,
            skipParams: ['emailPlanId'],
          }),
        }),
    };

    // Go to the corresponding route if stepId is valid
    stepActions[stepId]?.();
  };

  private validateAndCleanRoute(route: string): string | null {
    const cleanedRoute = route.replace('/v2', '').replace('/purchase/', '');
    const cleanRoute = cleanedRoute.startsWith('/payment-plan')
      ? cleanedRoute.replace(/^\/payment-plan/, 'payment-plan')
      : cleanedRoute;

    const validRoutes: readonly string[] = [
      myAccountAppPaths.addOns,
      myAccountAppPaths.checkout,
      myAccountAppPaths.paymentPlan,
      myAccountAppPaths.paymentPlanSelect,
      myAccountAppPaths.paymentPlanCheckout,
    ];

    if (validRoutes.includes(cleanRoute)) {
      return cleanRoute;
    }

    return null;
  }

  private getUrlParamValues = async () => {
    const emailPlansManagerApi = await this.emailPlansManager.getApiWhenReady();
    const prepayManagerApi = await this.prepayManager.getApiWhenReady();
    const contactsTierManagerApi = await this.contactsTierManager.getApiWhenReady();
    const promoCodesManagerApi = await this.promoCodesManager.getApiWhenReady();
    const inboundApi = await this.inboundPromoCodesManager?.getApiWhenReady();
    const addOnsManagerApi = await this.addOnsManager?.getApiWhenReady();
    const nonProfitManagerApi = await this.nonProfitManager.getApiWhenReady();
    const payflowTopBarApi = await this.payflowTopBarManager.getApiWhenReady();

    const isSmsBundle = this.payflowProgressBarManager.flags?.isSmsBundle ?? false;

    return {
      selectedEmailPlanId: emailPlansManagerApi.getSelectedPlanId(),
      paymentPeriod: prepayManagerApi.getPrepay(),
      selectedContactTierMin: contactsTierManagerApi.getContactTier(),
      isNonProfit: nonProfitManagerApi.getIsNonProfit(),
      promoCodes: [
        ...new Set([
          ...(inboundApi?.getInboundPromoCodes() ?? []),
          ...this.orderSummaryManager.getNewPromotionCodeNamesFromPayload(),
          ...promoCodesManagerApi.getPromoCodesToBeValidated(),
        ]),
      ],
      selectedAddOns: addOnsManagerApi?.getSelectedAddons() ?? [],
      returnUrl: payflowTopBarApi.getReturnUrl(),
      returnLabel: payflowTopBarApi.getReturnLabel(),
      isSmsBundle,
    };
  };
}
